import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import {
  Paper,
  IconButton,
  Typography
} from '@material-ui/core'
import Icon from '../../../../components/atoms/Icon'
import { useAttachment, LAYOUT_PRESETS } from '../contexts/AttachmentContext'
import PanelToolbar from './PanelToolbar'

const MIN_PANEL_PERCENT = 0.15
const MAX_PANEL_PERCENT = 0.85

const useStyles = makeStyles((theme) => ({
  panelWrapper: {
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    animation: '$slideIn 0.3s ease-out',
    position: 'relative',
    transition: 'flex 0.3s ease',
    minWidth: 0
  },
  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: 'translateX(100px)'
    },
    to: {
      opacity: 1,
      transform: 'translateX(0)'
    }
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 6,
    height: '100%',
    cursor: 'col-resize',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover $resizeIndicator': {
      opacity: 1
    }
  },
  resizeIndicator: {
    width: 3,
    height: 40,
    borderRadius: 2,
    backgroundColor: 'var(--dai-color-border-accent, #90caf9)',
    opacity: 0,
    transition: 'opacity 150ms ease'
  },
  resizeHandleActive: {
    '& $resizeIndicator': {
      opacity: 1,
      backgroundColor: 'var(--dai-color-border-accent-hl, #42a5f5)'
    }
  },
  panel: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    borderLeft: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    boxShadow: '-2px 0 4px rgba(0, 0, 0, 0.1)'
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--padding-md)',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'var(--color-texture-300)',
    flexShrink: 0
  },
  panelContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  closeButton: {
    marginLeft: theme.spacing(1)
  }
}))

export function AttachmentPanel ({ open, onClose, title, children, viewerActions, onPopout }) {
  const classes = useStyles()
  const { effectivePreset, customRatio, setCustomRatio, layoutMode } = useAttachment()
  const [isDragging, setIsDragging] = useState(false)
  const startXRef = useRef(0)
  const containerWidthRef = useRef(0)
  const startPanelFractionRef = useRef(0)

  const handleMouseDown = useCallback((e) => {
    e.preventDefault()
    const container = e.currentTarget.closest('[class*="chatContainer"]') || e.currentTarget.parentElement?.parentElement
    if (!container) return
    containerWidthRef.current = container.offsetWidth
    startXRef.current = e.clientX
    const preset = customRatio || LAYOUT_PRESETS[layoutMode] || LAYOUT_PRESETS.sidebar
    startPanelFractionRef.current = preset.panelFlex / (preset.chatFlex + preset.panelFlex)
    setIsDragging(true)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [customRatio, layoutMode])

  const handleDoubleClick = useCallback(() => {
    setCustomRatio(null)
  }, [setCustomRatio])

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e) => {
      const delta = startXRef.current - e.clientX
      const deltaFraction = delta / containerWidthRef.current
      const panelFraction = Math.min(MAX_PANEL_PERCENT, Math.max(MIN_PANEL_PERCENT, startPanelFractionRef.current + deltaFraction))
      const chatFraction = 1 - panelFraction
      setCustomRatio({
        chatFlex: Math.round(chatFraction * 100),
        panelFlex: Math.round(panelFraction * 100)
      })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, setCustomRatio])

  if (!open) return null

  return (
    <div
      className={classes.panelWrapper}
      style={{
        flex: effectivePreset.panelFlex,
        transition: customRatio ? 'none' : undefined
      }}
    >
      <div
        className={`${classes.resizeHandle} ${isDragging ? classes.resizeHandleActive : ''}`}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
      >
        <div className={classes.resizeIndicator} />
      </div>
      <Paper
        className={classes.panel}
        elevation={0}
      >
        <div className={classes.panelHeader}>
          <Typography className='font-serif font-size-lg' variant='h6'>
            {title || 'Attachment'}
          </Typography>
          <IconButton
            onClick={onClose}
            className={classes.closeButton}
            size='small'
            aria-label='Close attachment viewer'
          >
            <Icon name='close' />
          </IconButton>
        </div>

        <PanelToolbar viewerActions={viewerActions} onPopout={onPopout} />

        <div className={classes.panelContent}>
          {children}
        </div>
      </Paper>
    </div>
  )
}

AttachmentPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
  viewerActions: PropTypes.node,
  onPopout: PropTypes.func
}
